import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { AppUser } from 'Src/views/App/App';
import { getSchoolUnionSchools } from 'Src/helpers/service/adminSU/schoolsTable';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { TOURNAMENT_SCHOOL_LOGO_FALLBACK } from 'Src/consts/tournament';
import './SchoolUnionSelectSchoolForm.scss';

interface SchoolUnionSelectSchoolFormProps {
  user: AppUser;
  isOpen: boolean;
  onClose: () => void;
  onContinue?: (schoolId: string) => void;
  isMultiSelect?: boolean;
  selectedSchoolIds?: string[];
  onContinueMultiple?: (schoolIds: string[]) => void;
  title?: string;
  description?: string;
  continueText?: string;
  presentation?: 'modal' | 'page';
  onDirtyChange?: (dirty: boolean) => void;
}

export const SchoolUnionSelectSchoolForm: React.FC<SchoolUnionSelectSchoolFormProps> = ({
  user,
  isOpen,
  onClose,
  onContinue,
  isMultiSelect = false,
  selectedSchoolIds = [],
  onContinueMultiple,
  title = 'Choose a school',
  description = 'Select the member school the new student belongs to.',
  continueText = 'Continue',
  presentation = 'modal',
  onDirtyChange
}) => {
  const [searchText, setSearchText] = useState('');
  const [suggestedSchools, setSuggestedSchools] = useState<any[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>(isMultiSelect ? selectedSchoolIds.map(String) : []);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const requestSequence = useRef(0);
  useEffect(() => {
    onDirtyChange?.(isOpen && [...selectedIds].sort().join('|') !== [...selectedSchoolIds.map(String)].sort().join('|'));
  }, [selectedIds.join('|'), selectedSchoolIds.join('|'), isOpen, onDirtyChange]);

  useEffect(() => {
    if (!isOpen) {
      requestSequence.current += 1;
      return;
    }

    setSearchText('');
    setSuggestedSchools([]);
    setSelectedIds(isMultiSelect ? selectedSchoolIds.map(String) : []);
    setErrorMessage('');
  }, [isOpen, isMultiSelect, selectedSchoolIds.join('|')]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const query = searchText.trim();
    const sequence = ++requestSequence.current;

    if (query.length === 1) {
      setSuggestedSchools([]);
      setIsLoading(false);
      setErrorMessage('');
      return undefined;
    }

    setIsLoading(true);
    setErrorMessage('');

    const timeoutId = window.setTimeout(() => {
      const filter = {
        where: query
          ? {
              name: { like: query, options: 'i' }
            }
          : {},
        limit: isMultiSelect ? 100 : 12,
        skip: 0,
        order: 'name ASC'
      };

      getSchoolUnionSchools(user, filter)
        .then(results => {
          if (sequence !== requestSequence.current) return;
          setSuggestedSchools(Array.isArray(results) ? results : []);
          setIsLoading(false);
        })
        .catch(error => {
          if (sequence !== requestSequence.current) return;
          console.error('Error fetching school suggestions:', error);
          setSuggestedSchools([]);
          setIsLoading(false);
          setErrorMessage('Schools could not be loaded. Please try again.');
        });
    }, query ? 250 : 0);

    return () => window.clearTimeout(timeoutId);
  }, [isOpen, searchText, user, isMultiSelect]);

  const getSchoolId = (school: any): string => String(school.id || school._id || '');

  const getSchoolPostcode = (school: any): string => {
    if (typeof school.postcode === 'string') return school.postcode;
    return school.postcode?.postcode || school.postcode?.postcodeNoSpaces || '';
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
    if (!isMultiSelect) setSelectedIds([]);
  };

  const handleSelectSchool = (schoolId: string) => {
    if (!isMultiSelect) {
      setSelectedIds([schoolId]);
      return;
    }

    setSelectedIds(current =>
      current.includes(schoolId) ? current.filter(id => id !== schoolId) : [...current, schoolId]
    );
  };

  const handleContinue = () => {
    if (selectedIds.length === 0) return;
    if (isMultiSelect) {
      onContinueMultiple && onContinueMultiple(selectedIds);
      return;
    }
    onContinue && onContinue(selectedIds[0]);
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter' || isLoading) return;

    event.preventDefault();
    if (selectedIds.length > 0) {
      handleContinue();
      return;
    }

    if (suggestedSchools.length === 1) {
      const schoolId = getSchoolId(suggestedSchools[0]);
      if (isMultiSelect) handleSelectSchool(schoolId);
      else onContinue && onContinue(schoolId);
    }
  };

  const content = (
      <div className="schoolUnionSchoolPicker__content">
        <div className="schoolUnionSchoolPicker__intro">
          <p>{description}</p>
          <label htmlFor="school-union-school-search">School name</label>
          <div className="schoolUnionSchoolPicker__search">
            <input
              id="school-union-school-search"
              type="search"
              value={searchText}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search by school name"
              autoComplete="off"
              autoFocus
            />
            {searchText && (
              <button
                type="button"
                onClick={() => {
                  setSearchText('');
                  if (!isMultiSelect) setSelectedIds([]);
                }}
                aria-label="Clear school search"
              >
                ×
              </button>
            )}
          </div>
        </div>

        {isMultiSelect && (
          <div className="schoolUnionSchoolPicker__selectionSummary">
            <span>
              <strong>{selectedIds.length}</strong> {selectedIds.length === 1 ? 'school' : 'schools'} selected
            </span>
            {selectedIds.length > 0 && (
              <button type="button" onClick={() => setSelectedIds([])}>
                Clear selection
              </button>
            )}
          </div>
        )}

        <div className="schoolUnionSchoolPicker__results" role="listbox" aria-label="Member schools">
          {isLoading && (
            <div className="schoolUnionSchoolPicker__loading" role="status">
              <span />
              Loading schools…
            </div>
          )}

          {!isLoading && searchText.trim().length === 1 && (
            <div className="schoolUnionSchoolPicker__empty">
              <strong>Keep typing</strong>
              <span>Enter at least two letters to search.</span>
            </div>
          )}

          {!isLoading && errorMessage && (
            <div className="schoolUnionSchoolPicker__error" role="alert">
              {errorMessage}
            </div>
          )}

          {!isLoading && !errorMessage && searchText.trim().length !== 1 && suggestedSchools.length === 0 && (
            <div className="schoolUnionSchoolPicker__empty">
              <strong>No schools found</strong>
              <span>Try a different school name.</span>
            </div>
          )}

          {!isLoading &&
            suggestedSchools.map(school => {
              const schoolId = getSchoolId(school);
              const isSelected = selectedIds.includes(schoolId);
              const postcode = getSchoolPostcode(school);

              return (
                <button
                  key={schoolId}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={`schoolUnionSchoolPicker__school${isSelected ? ' is-selected' : ''}`}
                  onClick={() => handleSelectSchool(schoolId)}
                  onDoubleClick={() => !isMultiSelect && onContinue && onContinue(schoolId)}
                >
                  <img
                    src={school.pic || school.picUrl || TOURNAMENT_SCHOOL_LOGO_FALLBACK}
                    alt=""
                    onError={event => {
                      event.currentTarget.src = TOURNAMENT_SCHOOL_LOGO_FALLBACK;
                    }}
                  />
                  <span className="schoolUnionSchoolPicker__schoolText">
                    <strong>{school.name}</strong>
                    {(postcode || school.domain) && (
                      <small>{[postcode, school.domain].filter(Boolean).join(' · ')}</small>
                    )}
                  </span>
                  <span className="schoolUnionSchoolPicker__selection" aria-hidden="true">
                    {isSelected ? '✓' : ''}
                  </span>
                </button>
              );
            })}
        </div>

        <div className="schoolUnionSchoolPicker__actions">
          <button type="button" className="is-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="is-primary" onClick={handleContinue} disabled={selectedIds.length === 0}>
            {continueText}
          </button>
        </div>
      </div>
  );
  if (presentation === 'page') return isOpen ? <section className="schoolUnionSchoolPickerPage"><header><h2>{title}</h2></header>{content}</section> : null;
  return <SimpleModal isOpen={isOpen} onCloseClick={onClose} onBackdropClick={onClose} title={title} showFooter={false}
    customClass="schoolUnionSchoolPicker" bodyClassName="schoolUnionSchoolPicker__modalBody" dialogStyle={{maxWidth: 620}}>{content}</SimpleModal>;
};
